/**
 * HandoverPreviewDrawer — slide-in peek at a departing teammate's captured
 * memory BEFORE kicking off the full pack generation (which is slow +
 * writes artifacts). Reuses the same aggregation as the real pack.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  X, FileDown, GitPullRequest, FileText, Users, Loader2,
  Sparkles, Notebook, AlertCircle, ArrowRight,
} from "lucide-react";

function tone(pct) {
  if (pct >= 70) return "text-emerald-500 border-emerald-500/40 bg-emerald-500/10";
  if (pct >= 40) return "text-amber-500 border-amber-500/40 bg-amber-500/10";
  return "text-red-500 border-red-500/40 bg-red-500/10";
}

export default function HandoverPreviewDrawer({
  subjectUserId,
  subjectName,
  isOpen,
  onClose,
  onGenerateFull,   // () => void — parent kicks off the 202 flow
  generating,       // boolean — disable the CTA while the pack runs
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !subjectUserId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/memory/handover/${subjectUserId}/preview`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Could not load preview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, subjectUserId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9995] bg-background/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="handover-preview-overlay"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-popover border-l border-border/60 shadow-2xl flex flex-col"
        data-testid="handover-preview-drawer"
      >
        <header className="px-6 py-5 border-b border-border/60 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-amber-500">Handover preview</p>
            <h2 className="font-display text-xl mt-1">{subjectName}'s captured memory</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Peek before generating the full PDF pack.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close preview"
            data-testid="handover-preview-close"
            className="text-muted-foreground hover:text-foreground rounded-full p-1 shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-auto px-6 py-5 space-y-6">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="handover-preview-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Crunching the memory…
            </div>
          )}
          {error && (
            <div
              className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              data-testid="handover-preview-error"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
          {data && !loading && (
            <>
              {/* Coverage score */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="eyebrow">Coverage</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {data.counts.total} entries captured · expected ~{data.counts.total < 10 ? "30+" : "40+"} for a strong handover
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-full border px-3 py-1 font-display text-2xl ${tone(data.coverage_score)}`}
                  data-testid="handover-preview-coverage"
                >
                  {data.coverage_score}%
                </span>
              </div>

              {/* Counts grid */}
              <div className="grid grid-cols-2 gap-2" data-testid="handover-preview-counts">
                {[
                  { k: "decisions",  icon: GitPullRequest, label: "Decisions" },
                  { k: "processes",  icon: FileText,       label: "Processes" },
                  { k: "notes",      icon: Notebook,       label: "Notes" },
                  { k: "activities", icon: Sparkles,       label: "Activities" },
                ].map(({ k, icon: Icon, label }) => (
                  <div key={k} className="border border-border/60 rounded-md px-3 py-2.5 flex items-center gap-3">
                    <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="font-display text-xl leading-none">{data.counts[k] || 0}</p>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Top decisions */}
              {data.top_decisions?.length > 0 && (
                <section>
                  <p className="eyebrow mb-2">Top decisions</p>
                  <ul className="space-y-2" data-testid="handover-preview-decisions">
                    {data.top_decisions.map((d) => (
                      <li key={d.id} className="text-sm border border-border/40 rounded-md px-3 py-2 bg-secondary/30">
                        <p className="font-medium line-clamp-1">{d.title}</p>
                        {d.content && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.content}</p>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Top processes */}
              {data.top_processes?.length > 0 && (
                <section>
                  <p className="eyebrow mb-2">Processes they own</p>
                  <ul className="space-y-1.5" data-testid="handover-preview-processes">
                    {data.top_processes.map((p) => (
                      <li key={p.id} className="text-sm flex items-center gap-2 text-muted-foreground">
                        <FileText className="w-3 h-3 shrink-0" />
                        <span className="line-clamp-1">{p.title}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Stakeholders */}
              {data.stakeholders?.length > 0 && (
                <section>
                  <p className="eyebrow mb-2 flex items-center gap-1.5">
                    <Users className="w-3 h-3" /> Key stakeholders
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-testid="handover-preview-stakeholders">
                    {data.stakeholders.slice(0, 10).map((s) => (
                      <span key={s} className="text-xs border border-border/60 rounded-full px-2.5 py-1 text-muted-foreground">
                        {s}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Top tags */}
              {data.top_tags?.length > 0 && (
                <section>
                  <p className="eyebrow mb-2">Top tags</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="handover-preview-tags">
                    {data.top_tags.map((t) => (
                      <span key={t} className="text-[11px] font-mono border border-border/60 rounded-full px-2.5 py-0.5 text-muted-foreground">
                        {t}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Open items */}
              {data.open_items_count > 0 && (
                <section className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3">
                  <p className="text-xs uppercase tracking-wider text-amber-500 mb-1.5">
                    {data.open_items_count} open suggestion{data.open_items_count === 1 ? "" : "s"} still pending
                  </p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {data.open_items_preview.map((s) => (
                      <li key={s.id} className="line-clamp-1">• {s.reason || "Suggested edit"}</li>
                    ))}
                  </ul>
                </section>
              )}

              {data.counts.total === 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground" data-testid="handover-preview-empty">
                  Nothing captured yet. A handover pack for {subjectName} would be mostly empty — encourage them to start capturing.
                </div>
              )}
            </>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-border/60 flex gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full flex-1" data-testid="handover-preview-cancel">
            Close
          </Button>
          <Button
            onClick={() => { onGenerateFull?.(); onClose(); }}
            disabled={generating || !data || data.counts.total === 0}
            className="rounded-full flex-1"
            data-testid="handover-preview-generate"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
              : <><FileDown className="w-4 h-4 mr-2" /> Generate full pack <ArrowRight className="w-3.5 h-3.5 ml-1" /></>}
          </Button>
        </footer>
      </aside>
    </div>
  );
}
